import React from 'react'
import ReactDOM from 'react-dom/client'
import Nuggets from './Nuggets.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import { initAuth, login } from './auth.js'

// PATH B — Keycloak bootstrap.
//
// Before mounting the app we run initAuth() which asks keycloak-js
// to check for an existing SSO session.
//   * If found  → app renders normally.
//   * If not    → we immediately redirect to Keycloak. No landing
//                 page in between — Nuggets is invite-only, there's
//                 nothing to browse anonymously, and the interstitial
//                 "Sign In" button was pure friction.
//   * If init errors (Keycloak down, /api/auth/config 500) → show a
//     retry screen so users have something to see rather than an
//     infinite reload loop.
function LoadingShell() {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0D1B2A',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif",
      fontSize: 12, letterSpacing: '0.06em',
    }}>Loading…</div>
  );
}

// Only shown if initAuth() throws — a genuine outage, not a "you're
// logged out" state.  Gives the user an escape hatch instead of an
// infinite refresh loop.
function AuthErrorScreen({ error }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: '#0D1B2A',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontFamily: "'Montserrat',sans-serif",
      gap: 16, padding: 24, textAlign: 'center',
    }}>
      <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: '-0.01em' }}>Sign-in service unavailable</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', maxWidth: 380, lineHeight: 1.55 }}>
        {error || 'Nuggets could not reach the authentication server.'}
      </div>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#E87722', border: 'none', color: '#fff',
          borderRadius: 6, padding: '10px 24px', cursor: 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 6,
        }}
      >Retry</button>
    </div>
  );
}

const rootEl = document.getElementById('root');
const root   = ReactDOM.createRoot(rootEl);

// Show loading until Keycloak init resolves.
root.render(<React.StrictMode><LoadingShell /></React.StrictMode>);

// One-shot guard so a hung SSO check can't put us in a redirect loop: we
// attempt the top-level login recovery once, and if that round-trip still
// times out we show the error screen instead of redirecting again.
const SSO_FALLBACK_KEY = 'kc_sso_fallback_attempted';

initAuth()
  .then((authenticated) => {
    // A clean check resolved — release the one-shot guard so a future hang
    // (e.g. after an explicit logout) can retry the redirect recovery.
    try { sessionStorage.removeItem(SSO_FALLBACK_KEY); } catch { /* private mode */ }
    if (authenticated) {
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <Nuggets />
          </ErrorBoundary>
        </React.StrictMode>
      );
    } else {
      // No session → straight to Keycloak.  The LoadingShell stays on
      // screen for the ~1s it takes the browser to redirect.
      login();
    }
  })
  .catch((err) => {
    // A hung silent-SSO check (SSO_TIMEOUT) is recoverable: a top-level
    // login redirect isn't subject to the hidden-iframe / third-party-cookie
    // limits that usually cause the hang, and if the realm is genuinely
    // misconfigured Keycloak shows its real error page instead of a dead
    // spinner. Try it once (guarded against loops), then fall back to the
    // error screen.
    let alreadyTried = false;
    try { alreadyTried = !!sessionStorage.getItem(SSO_FALLBACK_KEY); } catch { /* private mode */ }
    if (err?.code === 'SSO_TIMEOUT' && !alreadyTried) {
      console.warn('[main] silent SSO check timed out — falling back to top-level login redirect');
      try {
        sessionStorage.setItem(SSO_FALLBACK_KEY, '1');
        login();
        return;
      } catch (e) {
        console.error('[main] login redirect fallback failed:', e);
        // fall through to the error screen
      }
    }
    console.error('[main] Keycloak init failed:', err);
    root.render(
      <React.StrictMode>
        <AuthErrorScreen error={err?.message || String(err)} />
      </React.StrictMode>
    );
  });
